import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  School as AcademyIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminService, { Player, Coach } from '@services/adminService';
import CoachPermissionsPage from '@components/CoachPermissionsPage';
import { toast } from 'react-toastify';

interface Academy {
  id: string;
  name: string;
  description: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  head_coach_id?: string;
}

const AcademyManagement: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine which view to show based on URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const activeView = pathParts[pathParts.length - 1] || 'academies'; // 'academies', 'players', 'coaches', 'permissions'
  
  // Academy management state
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAcademy, setEditingAcademy] = useState<Academy | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    contact_email: '',
    contact_phone: '',
  });
  
  // Players state
  const [players, setPlayers] = useState<Player[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  
  // Coaches state
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  
  // Add Junior Coach Dialog state
  const [addJuniorCoachDialogOpen, setAddJuniorCoachDialogOpen] = useState(false);
  const [editCoachDialogOpen, setEditCoachDialogOpen] = useState(false);
  const [editingCoach, setEditingCoach] = useState<any>(null);
  const [generatedCoachPassword, setGeneratedCoachPassword] = useState<string | null>(null);
  const [searchingCoach, setSearchingCoach] = useState(false);
  const [foundCoach, setFoundCoach] = useState<any>(null);
  const [juniorCoachFormData, setJuniorCoachFormData] = useState({
    coachIdCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    assignedSports: [] as string[],
  });
  const [editCoachFormData, setEditCoachFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    assignedSports: [] as string[],
  });
  
  // Add Player Dialog state
  const [addPlayerDialogOpen, setAddPlayerDialogOpen] = useState(false);
  const [editPlayerDialogOpen, setEditPlayerDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<any>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [editPlayerFormData, setEditPlayerFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: 'male',
    country: '',
    primarySport: '',
    secondarySport: '',
    level: 'beginner',
  });
  const [searchingPlayer, setSearchingPlayer] = useState(false);
  const [foundPlayer, setFoundPlayer] = useState<any>(null);
  const [isBelow18, setIsBelow18] = useState(false);
  const [playerFormData, setPlayerFormData] = useState({
    playerIdCode: '',
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: 'male',
    country: '',
    primarySport: '',
    secondarySport: '',
    grade: '',
    section: '',
    level: 'beginner',
    // Parent fields
    parent_email: '',
    parent_phone: '',
    parent_country: '',
    father_name: '',
    mother_name: '',
  });
  
  const sportsOptions = ['Badminton', 'Tennis', 'Table Tennis', 'Squash'];
  const playerSports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
  const levels = ['beginner', 'intermediate', 'advanced'];
  const genders = ['male', 'female', 'other'];

  useEffect(() => {
    if (activeView === 'academies' || activeView === 'academy-management') {
      loadAcademies();
    } else if (activeView === 'players') {
      loadPlayers();
    } else if (activeView === 'coaches') {
      loadCoaches();
    }
  }, [activeView, user]);

  // Auto-refresh coaches list when page becomes visible (e.g., after tab switch or window focus)
  useEffect(() => {
    if (activeView === 'coaches') {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log('🔄 Page visible - refreshing coaches list...');
          loadCoaches();
        }
      };

      const handleFocus = () => {
        console.log('🔄 Window focused - refreshing coaches list...');
        loadCoaches();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [activeView]);

  // Auto-refresh academies list when page becomes visible (e.g., after tab switch or window focus)
  useEffect(() => {
    if (activeView === 'academies' || activeView === 'academy-management') {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log('🔄 Page visible - refreshing academies list...');
          loadAcademies();
        }
      };

      const handleFocus = () => {
        console.log('🔄 Window focused - refreshing academies list...');
        loadAcademies();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [activeView]);

  // Auto-refresh players list when page becomes visible (e.g., after tab switch or window focus)
  useEffect(() => {
    if (activeView === 'players') {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log('🔄 Page visible - refreshing players list...');
          loadPlayers();
        }
      };

      const handleFocus = () => {
        console.log('🔄 Window focused - refreshing players list...');
        loadPlayers();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [activeView]);
  
  const loadPlayers = async () => {
    let coachId = (user as any)?.coachId;
    
    // If coachId is not in user object, try to fetch it from the API
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
          // Update user object with coachId
          const updatedUser = { ...user, coachId: data.data.id, coachIdCode: data.data.coachIdCode };
          const { setUser } = useAuthStore.getState();
          setUser(updatedUser);
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }
    
    if (!coachId) {
      setError('Coach ID not found. Please contact support if this issue persists.');
      return;
    }
    
    setLoadingPlayers(true);
    try {
      console.log(`📡 Loading players for coach ${coachId}...`);
      const response = await AdminService.getPlayersForCoach(coachId);
      console.log('📦 API Response:', response);
      const playersData = response.data || [];
      console.log(`✅ Loaded ${playersData.length} players`);
      
      // Log lastLoginAt for each player to debug
      playersData.forEach((player: any) => {
        if (player.playerIdCode === 'P1768147465474') {
          console.log('🔍 Player P1768147465474 data:', {
            id: player.id,
            playerIdCode: player.playerIdCode,
            user: player.user,
            lastLoginAt: player.user?.lastLoginAt,
            hasLastLoginAt: !!player.user?.lastLoginAt
          });
        }
      });
      
      setPlayers(playersData);
    } catch (err: any) {
      console.error('❌ Error loading players:', err);
      setError('Failed to load players');
    } finally {
      setLoadingPlayers(false);
    }
  };
  
  const loadCoaches = async () => {
    let coachId = (user as any)?.coachId;
    
    // If coachId is not in user object, try to fetch it from the API
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
          // Update user object with coachId
          const updatedUser = { ...user, coachId: data.data.id, coachIdCode: data.data.coachIdCode };
          const { setUser } = useAuthStore.getState();
          setUser(updatedUser);
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }
    
    if (!coachId) {
      setError('Coach ID not found. Please contact support if this issue persists.');
      setLoadingCoaches(false);
      return;
    }
    
    setLoadingCoaches(true);
    setError(null);
    try {
      console.log(`📡 Loading coaches for coach ${coachId}...`);
      
      const allCoaches: any[] = [];
      
      // First, add the logged-in coach
      try {
        const coachMeResponse = await fetch(`/api/coaches/me?userId=${user.id}`);
        const coachMeData = await coachMeResponse.json();
        if (coachMeData.success && coachMeData.data) {
          const coachData = coachMeData.data;
          allCoaches.push({
            id: coachData.id,
            userId: coachData.userId || user?.id,
            coachIdCode: coachData.coachIdCode || (user as any)?.coachIdCode,
            user: {
              id: coachData.userId || user?.id,
              email: coachData.user.email,
              firstName: coachData.user.firstName,
              lastName: coachData.user.lastName,
              isActive: coachData.user.isActive,
              lastLoginAt: coachData.user.lastLoginAt,
            },
            primarySport: coachData.primarySport,
            academyName: coachData.academyName,
            assignedSports: coachData.assignedSports || [],
            isCurrentUser: true, // Flag to identify logged-in coach
          });
        }
      } catch (err) {
        console.error('Error fetching current coach data:', err);
      }
      
      // Then load junior coaches from hierarchy API
      const response = await fetch(`/api/coaches/hierarchy/${coachId}`);
      const data = await response.json();
      
      if (data.success && data.data?.juniorCoaches) {
        const juniorCoaches = data.data.juniorCoaches.map((coach: any) => ({
          id: coach.id,
          userId: coach.userId || '',
          coachIdCode: coach.coachIdCode || coach.coach_id_code,
          user: {
            id: coach.userId || coach.user?.id,
            email: coach.user?.email || coach.email,
            firstName: coach.firstName || coach.first_name || coach.user?.firstName,
            lastName: coach.lastName || coach.last_name || coach.user?.lastName,
            isActive: coach.user?.isActive !== false,
            lastLoginAt: coach.user?.lastLoginAt,
          },
          primarySport: coach.primarySport || coach.primary_sport,
          academyName: coach.academyName || coach.academy_name,
          assignedSports: coach.assignedSports || coach.assigned_sports || [],
          isCurrentUser: false,
        }));
        
        // Add junior coaches (avoid duplicates)
        juniorCoaches.forEach((coach: any) => {
          if (!allCoaches.find(c => c.id === coach.id)) {
            allCoaches.push(coach);
          }
        });
        
        console.log(`✅ Loaded ${allCoaches.length} coaches (1 current + ${juniorCoaches.length} junior)`);
        setCoaches(allCoaches);
      } else {
        console.log('No junior coaches found, showing only current coach');
        setCoaches(allCoaches);
      }
      setError(null);
    } catch (err: any) {
      console.error('❌ Error loading coaches:', err);
      setError('Failed to load coaches');
      setCoaches([]);
    } finally {
      setLoadingCoaches(false);
    }
  };

  const loadAcademies = async () => {
    setLoading(true);
    setError(null);
    try {
      let coachId = (user as any)?.coachId;
      
      // If coachId is not in user object, try to fetch it from the API
      if (!coachId && user?.id) {
        try {
          const response = await fetch(`/api/coaches/me?userId=${user.id}`);
          const data = await response.json();
          if (data.success && data.data) {
            coachId = data.data.id;
            // Update user object with coachId
            const updatedUser = { ...user, coachId: data.data.id, coachIdCode: data.data.coachIdCode };
            const { setUser } = useAuthStore.getState();
            setUser(updatedUser);
          }
        } catch (err) {
          console.error('Error fetching coach data:', err);
        }
      }
      
      if (!coachId) {
        setError('Coach ID not found. Please contact support if this issue persists.');
        setLoading(false);
        return;
      }
      
      console.log(`📡 Loading academies for coach ${coachId}...`);
      const response = await fetch(`/api/coaches/${coachId}/academies`);
      const data = await response.json();
      
      if (data.success && data.data) {
        // Map API response to Academy interface
        const academiesData: Academy[] = data.data.map((academy: any) => ({
          id: academy.id || academy.academyId,
          name: academy.name || academy.academyName,
          description: academy.description || '',
          address: academy.address || `${academy.city || ''}${academy.city && academy.country ? ', ' : ''}${academy.country || ''}`.trim(),
          contact_email: academy.email || '',
          contact_phone: academy.phone || '',
          head_coach_id: academy.head_coach_id,
        }));
        
        console.log(`✅ Loaded ${academiesData.length} academies`);
        setAcademies(academiesData);
      } else {
        console.log('No academies found');
        setAcademies([]);
      }
    } catch (err: any) {
      console.error('❌ Error loading academies:', err);
      setError('Failed to load academies');
      setAcademies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/coach/dashboard');
  };

  const handleSearchByCoachId = async () => {
    if (!juniorCoachFormData.coachIdCode) {
      toast.error('Please enter a Coach ID');
      return;
    }

    setSearchingCoach(true);
    try {
      const response = await fetch(`/api/coaches/by-id/${juniorCoachFormData.coachIdCode}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const coach = result.data;
        setJuniorCoachFormData({
          ...juniorCoachFormData,
          firstName: coach.firstName || coach.first_name || juniorCoachFormData.firstName,
          lastName: coach.lastName || coach.last_name || juniorCoachFormData.lastName,
          email: coach.email || juniorCoachFormData.email,
          phone: coach.phone || juniorCoachFormData.phone,
          assignedSports: coach.assignedSports || coach.assigned_sports || juniorCoachFormData.assignedSports,
        });
        setFoundCoach(coach);
        toast.success('Coach information loaded successfully');
      } else {
        toast.error(result.message || 'Coach not found');
        setFoundCoach(null);
      }
    } catch (err: any) {
      console.error('Error searching coach:', err);
      toast.error('Failed to search coach');
      setFoundCoach(null);
    } finally {
      setSearchingCoach(false);
    }
  };

  const handleAddJuniorCoach = async () => {
    if (!juniorCoachFormData.firstName || !juniorCoachFormData.lastName || !juniorCoachFormData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    let coachId = (user as any)?.coachId;
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }

    if (!coachId) {
      toast.error('Coach ID not found. Please contact support.');
      return;
    }

    setLoadingCoaches(true);
    try {
      const response = await fetch(`/api/coaches/${coachId}/junior-coaches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: juniorCoachFormData.firstName,
          lastName: juniorCoachFormData.lastName,
          email: juniorCoachFormData.email,
          phone: juniorCoachFormData.phone || undefined,
          assignedSports: juniorCoachFormData.assignedSports,
          coachIdCode: juniorCoachFormData.coachIdCode || undefined,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Junior coach added successfully!');
        setAddJuniorCoachDialogOpen(false);
        resetJuniorCoachForm();
        loadCoaches();
      } else {
        toast.error(result.message || 'Failed to add junior coach');
      }
    } catch (err: any) {
      console.error('Error adding junior coach:', err);
      toast.error('Failed to add junior coach');
    } finally {
      setLoadingCoaches(false);
    }
  };

  const handleSearchByPlayerId = async () => {
    if (!playerFormData.playerIdCode) {
      toast.error('Please enter a Player ID');
      return;
    }

    setSearchingPlayer(true);
    try {
      const response = await fetch(`/api/players/by-id/${playerFormData.playerIdCode}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const player = result.data;
        // Determine if player is below 18 based on registered_by_parent or calculate from dateOfBirth
        let playerIsBelow18 = player.registeredByParent || false;
        if (!playerIsBelow18 && player.dateOfBirth) {
          const birthDate = new Date(player.dateOfBirth);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          playerIsBelow18 = age < 18;
        }
        setIsBelow18(playerIsBelow18);

        // Populate form data with player information
        setPlayerFormData({
          ...playerFormData,
          firstName: player.firstName || '',
          lastName: player.lastName || '',
          email: player.email || '',
          dateOfBirth: player.dateOfBirth || '',
          gender: player.gender || 'male',
          country: player.country || '',
          primarySport: player.primarySport || '',
          secondarySport: player.secondarySport || '',
          grade: player.grade || '',
          section: player.section || '',
          level: player.level || player.skillLevel || 'beginner',
          // Parent fields - use the correct field names from API response
          parent_email: player.parentEmail || '',
          parent_phone: player.parentPhone || '',
          parent_country: player.parentCountry || '',
          father_name: player.fatherName || '',
          mother_name: player.motherName || '',
        });
        setFoundPlayer(player);
        toast.success('Player information loaded successfully');
      } else {
        toast.error(result.message || 'Player not found');
        setFoundPlayer(null);
      }
    } catch (err: any) {
      console.error('Error searching player:', err);
      toast.error('Failed to search player');
      setFoundPlayer(null);
    } finally {
      setSearchingPlayer(false);
    }
  };

  const handleAddPlayer = async () => {
    // Validate required fields based on age
    if (!playerFormData.firstName || !playerFormData.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (isBelow18) {
      if (!playerFormData.parent_email || !playerFormData.parent_phone || !playerFormData.parent_country || 
          !playerFormData.father_name || !playerFormData.mother_name) {
        toast.error('For players under 18, parent email, phone, country, father name, and mother name are required');
        return;
      }
    } else {
      if (!playerFormData.email) {
        toast.error('Email is required for players 18+');
        return;
      }
    }

    let coachId = (user as any)?.coachId;
    if (!coachId && user?.id) {
      try {
        const response = await fetch(`/api/coaches/me?userId=${user.id}`);
        const data = await response.json();
        if (data.success && data.data) {
          coachId = data.data.id;
        }
      } catch (err) {
        console.error('Error fetching coach data:', err);
      }
    }

    if (!coachId) {
      toast.error('Coach ID not found. Please contact support.');
      return;
    }

    // Get academies for this coach
    let academyId = null;
    try {
      const academiesResponse = await fetch(`/api/coaches/${coachId}/academies`);
      const academiesData = await academiesResponse.json();
      if (academiesData.success && academiesData.data && academiesData.data.length > 0) {
        academyId = academiesData.data[0].academyId || academiesData.data[0].id;
      }
    } catch (err) {
      console.error('Error fetching academies:', err);
    }

    setLoadingPlayers(true);
    try {
      let playerId = null;
      let playerCreated = false;

      // Step 1: Create or find the player
      if (playerFormData.playerIdCode) {
        // If player ID is provided, search for existing player
        try {
          const searchResponse = await fetch(`/api/players/by-id/${playerFormData.playerIdCode}`);
          const searchResult = await searchResponse.json();
          if (searchResult.success && searchResult.data) {
            playerId = searchResult.data.id;
          }
        } catch (err) {
          console.error('Error searching player:', err);
        }
      }

      // If player not found by ID, create new player
      if (!playerId) {
        try {
          // Generate a temporary password (will be reset by player later)
          const tempPassword = 'temp-' + Math.random().toString(36).slice(-12) + Date.now().toString().slice(-6);
          
          // Prepare API data
          const apiData: any = {
            email: isBelow18 ? playerFormData.parent_email : playerFormData.email,
            password: tempPassword, // Temporary password
            firstName: playerFormData.firstName,
            lastName: playerFormData.lastName,
            dateOfBirth: playerFormData.dateOfBirth || undefined,
            gender: playerFormData.gender,
            country: playerFormData.country || undefined,
            primarySport: playerFormData.primarySport || undefined,
            secondarySport: playerFormData.secondarySport || undefined,
            grade: playerFormData.grade || undefined,
            section: playerFormData.section || undefined,
            level: playerFormData.level,
            is_below_18: isBelow18,
          };

          // Add parent fields if below 18
          if (isBelow18) {
            apiData.parent_email = playerFormData.parent_email;
            apiData.parent_phone = playerFormData.parent_phone;
            apiData.parent_country = playerFormData.parent_country;
            apiData.father_name = playerFormData.father_name;
            apiData.mother_name = playerFormData.mother_name;
          }

          const createResponse = await fetch(`/api/players`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(apiData),
          });

          const createResult = await createResponse.json();
          if (createResult.success && createResult.data) {
            playerId = createResult.data.id;
            playerCreated = true;
          } else {
            throw new Error(createResult.message || 'Failed to create player');
          }
        } catch (err: any) {
          console.error('Error creating player:', err);
          toast.error(err.message || 'Failed to create player');
          setLoadingPlayers(false);
          return;
        }
      }

      // Step 2: Assign player to coach
      if (playerId && coachId) {
        const sport = playerFormData.primarySport || 'General';
        const assignResponse = await fetch(`/api/coaches/${coachId}/assign-player`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            playerId: playerId,
            sport: sport,
            assignmentType: 'primary',
          }),
        });

        const assignResult = await assignResponse.json();
        if (assignResult.success) {
          toast.success(playerCreated ? 'Player created and assigned successfully!' : 'Player assigned successfully!');
          setAddPlayerDialogOpen(false);
          resetPlayerForm();
          loadPlayers();
        } else {
          toast.error(assignResult.message || 'Failed to assign player to coach');
        }
      } else {
        toast.error('Failed to get player or coach ID');
      }
    } catch (err: any) {
      console.error('Error adding player:', err);
      toast.error('Failed to add player');
    } finally {
      setLoadingPlayers(false);
    }
  };

  const resetJuniorCoachForm = () => {
    setJuniorCoachFormData({
      coachIdCode: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      assignedSports: [],
    });
    setFoundCoach(null);
  };

  const resetPlayerForm = () => {
    setIsBelow18(false);
    setPlayerFormData({
      playerIdCode: '',
      firstName: '',
      lastName: '',
      email: '',
      dateOfBirth: '',
      gender: 'male',
      country: '',
      primarySport: '',
      secondarySport: '',
      grade: '',
      section: '',
      level: 'beginner',
      parent_email: '',
      parent_phone: '',
      parent_country: '',
      father_name: '',
      mother_name: '',
    });
    setFoundPlayer(null);
  };

  const handleOpenDialog = (academy?: Academy) => {
    if (academy) {
      setEditingAcademy(academy);
      setFormData({
        name: academy.name,
        description: academy.description,
        address: academy.address,
        contact_email: academy.contact_email,
        contact_phone: academy.contact_phone,
      });
    } else {
      setEditingAcademy(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        contact_email: '',
        contact_phone: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAcademy(null);
    setFormData({
      name: '',
      description: '',
      address: '',
      contact_email: '',
      contact_phone: '',
    });
  };

  const handleSubmit = async () => {
    try {
      // TODO: Implement actual API call
      console.log('Submitting academy:', formData);
      setSuccess(editingAcademy ? 'Academy updated successfully!' : 'Academy created successfully!');
      handleCloseDialog();
      loadAcademies();
    } catch (err: any) {
      setError(err.message || 'Failed to save academy');
    }
  };

  const handleDelete = async (academyId: string) => {
    if (window.confirm('Are you sure you want to delete this academy?')) {
      try {
        // TODO: Implement actual API call
        console.log('Deleting academy:', academyId);
        setSuccess('Academy deleted successfully!');
        loadAcademies();
      } catch (err: any) {
        setError(err.message || 'Failed to delete academy');
      }
    }
  };

  // Render Permissions page
  if (activeView === 'permissions') {
    return <CoachPermissionsPage />;
  }

  // Render Coaches list
  if (activeView === 'coaches') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" component="h1">
              Coaches
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                console.log('🔄 Manual refresh triggered for coaches');
                loadCoaches();
              }}
              disabled={loadingCoaches}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddJuniorCoachDialogOpen(true)}
            >
              Add Junior Coach
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            {loadingCoaches ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Coach ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Assigned Sports</TableCell>
                      <TableCell>Onboarded Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {coaches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">No coaches found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      coaches.map((coach: any) => (
                        <TableRow key={coach.id}>
                          <TableCell>{coach.coachIdCode || coach.coach_id_code || 'N/A'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                              {coach.user?.firstName || coach.user?.first_name || ''} {coach.user?.lastName || coach.user?.last_name || ''}
                            </Box>
                          </TableCell>
                          <TableCell>{coach.user?.email || 'N/A'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {coach.assignedSports && coach.assignedSports.length > 0 ? (
                                coach.assignedSports.map((sport: string) => (
                                  <Chip key={sport} label={sport} size="small" color="primary" />
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary">None</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={coach.user?.lastLoginAt ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <CancelIcon sx={{ fontSize: 16 }} />}
                              label={coach.user?.lastLoginAt ? "Yes" : "No"} 
                              size="small" 
                              color={coach.user?.lastLoginAt ? "success" : "warning"}
                              title={coach.user?.lastLoginAt ? "Coach has logged in and is onboarded" : "Coach has not logged in yet"}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Coach">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingCoach(coach);
                                  setEditCoachFormData({
                                    firstName: coach.user?.firstName || '',
                                    lastName: coach.user?.lastName || '',
                                    email: coach.user?.email || '',
                                    phone: coach.phone || '',
                                    assignedSports: coach.assignedSports || [],
                                  });
                                  setEditCoachDialogOpen(true);
                                  setGeneratedCoachPassword(null);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add Junior Coach Dialog */}
        <Dialog open={addJuniorCoachDialogOpen} onClose={() => { setAddJuniorCoachDialogOpen(false); resetJuniorCoachForm(); }} maxWidth="md" fullWidth>
          <DialogTitle>Add Junior Coach</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    label="EncycGames Coach ID (Optional)"
                    value={juniorCoachFormData.coachIdCode}
                    onChange={(e) => setJuniorCoachFormData({ ...juniorCoachFormData, coachIdCode: e.target.value })}
                    helperText="If coach is already registered, enter their Coach ID here"
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSearchByCoachId}
                    disabled={!juniorCoachFormData.coachIdCode || searchingCoach}
                  >
                    {searchingCoach ? 'Searching...' : 'Search & Load'}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  value={juniorCoachFormData.firstName}
                  onChange={(e) => setJuniorCoachFormData({ ...juniorCoachFormData, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  value={juniorCoachFormData.lastName}
                  onChange={(e) => setJuniorCoachFormData({ ...juniorCoachFormData, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={juniorCoachFormData.email}
                  onChange={(e) => setJuniorCoachFormData({ ...juniorCoachFormData, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={juniorCoachFormData.phone}
                  onChange={(e) => setJuniorCoachFormData({ ...juniorCoachFormData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assigned Sports</InputLabel>
                  <Select
                    multiple
                    value={juniorCoachFormData.assignedSports}
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                      setJuniorCoachFormData({
                        ...juniorCoachFormData,
                        assignedSports: value
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {sportsOptions.map((sport) => (
                      <MenuItem key={sport} value={sport}>
                        <Checkbox checked={juniorCoachFormData.assignedSports.indexOf(sport) > -1} />
                        {sport}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddJuniorCoachDialogOpen(false); resetJuniorCoachForm(); }}>Cancel</Button>
            <Button onClick={handleAddJuniorCoach} variant="contained" disabled={loadingCoaches}>
              {loadingCoaches ? 'Adding...' : 'Add Coach'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Coach Dialog */}
        <Dialog open={editCoachDialogOpen} onClose={() => { setEditCoachDialogOpen(false); setEditingCoach(null); setGeneratedCoachPassword(null); }} maxWidth="md" fullWidth>
          <DialogTitle>Edit Coach</DialogTitle>
          <DialogContent>
            {editingCoach && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={editCoachFormData.firstName}
                    onChange={(e) => setEditCoachFormData({ ...editCoachFormData, firstName: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    value={editCoachFormData.lastName}
                    onChange={(e) => setEditCoachFormData({ ...editCoachFormData, lastName: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={editCoachFormData.email}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editCoachFormData.phone}
                    onChange={(e) => setEditCoachFormData({ ...editCoachFormData, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Assigned Sports</InputLabel>
                    <Select
                      multiple
                      value={editCoachFormData.assignedSports}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        setEditCoachFormData({
                          ...editCoachFormData,
                          assignedSports: value
                        });
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {sportsOptions.map((sport) => (
                        <MenuItem key={sport} value={sport}>
                          <Checkbox checked={editCoachFormData.assignedSports.indexOf(sport) > -1} />
                          {sport}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
            
            {/* Generate Password Section - Only show if coach hasn't logged in */}
            {editingCoach && !editingCoach.isCurrentUser && !editingCoach.user?.lastLoginAt && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Password Management
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This coach hasn't logged in yet. You can generate a password for them. Once they log in, they will control their own password.
                </Alert>
                {generatedCoachPassword ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Password Generated Successfully!
                    </Typography>
                    <Typography variant="body2" component="div">
                      <strong>New Password:</strong>
                      <Box 
                        sx={{ 
                          mt: 1, 
                          p: 2, 
                          bgcolor: 'grey.100', 
                          borderRadius: 1, 
                          fontFamily: 'monospace',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'text',
                          userSelect: 'text'
                        }}
                        onClick={(e) => {
                          const text = e.currentTarget.querySelector('span')?.textContent || generatedCoachPassword;
                          navigator.clipboard.writeText(text || '');
                          toast.success('Password copied to clipboard!');
                        }}
                      >
                        <span style={{ flex: 1 }}>{generatedCoachPassword}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(generatedCoachPassword || '');
                            toast.success('Password copied to clipboard!');
                          }}
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Click the password or copy icon to copy. Please share this password with the coach securely. They should change it after first login.
                    </Typography>
                  </Alert>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<SecurityIcon />}
                    onClick={async () => {
                      if (!editingCoach) return;
                      try {
                        const headCoachId = (user as any)?.coachId;
                        const response = await fetch(`/api/coaches/${editingCoach.id}/generate-password`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ headCoachId })
                        });
                        const data = await response.json();
                        if (data.success) {
                          setGeneratedCoachPassword(data.data.password);
                          toast.success('Password generated successfully!');
                        } else {
                          toast.error(data.message || 'Failed to generate password');
                        }
                      } catch (err: any) {
                        toast.error('Failed to generate password: ' + err.message);
                      }
                    }}
                    sx={{ mb: 2 }}
                  >
                    Generate Password
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  If the coach loses this password, they can use the "Forgot Password" feature on the login page with their Coach ID and email.
                </Typography>
              </Box>
            )}
            
            {editingCoach && (editingCoach.isCurrentUser || editingCoach.user?.lastLoginAt) && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Alert severity="info">
                  {editingCoach.isCurrentUser 
                    ? "This is your own account. You can manage your password from your profile page."
                    : "This coach has logged in and is active. They control their own password. If they need to reset it, they should use the 'Forgot Password' feature on the login page."}
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { 
              setEditCoachDialogOpen(false); 
              setEditingCoach(null); 
              setGeneratedCoachPassword(null);
            }}>
              {generatedCoachPassword ? 'Close' : 'Cancel'}
            </Button>
            {!generatedCoachPassword && (
              <Button
                variant="contained"
                onClick={async () => {
                  if (!editingCoach) return;
                  try {
                    const headCoachId = (user as any)?.coachId;
                    const response = await fetch(`/api/coaches/${editingCoach.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        firstName: editCoachFormData.firstName,
                        lastName: editCoachFormData.lastName,
                        phone: editCoachFormData.phone,
                        assignedSports: editCoachFormData.assignedSports,
                        headCoachId
                      })
                    });
                    const data = await response.json();
                    if (data.success) {
                      toast.success('Coach updated successfully!');
                      setEditCoachDialogOpen(false);
                      setEditingCoach(null);
                      setGeneratedCoachPassword(null);
                      loadCoaches();
                    } else {
                      toast.error(data.message || 'Failed to update coach');
                    }
                  } catch (err: any) {
                    toast.error('Failed to update coach: ' + err.message);
                  }
                }}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Render Players list
  if (activeView === 'players') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" component="h1">
              Players / Students
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                console.log('🔄 Manual refresh triggered');
                loadPlayers();
              }}
              disabled={loadingPlayers}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddPlayerDialogOpen(true)}
            >
              Add Player / Student
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            {loadingPlayers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Player ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Primary Sport</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Onboarded Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {players.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">No players found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      players.map((player: any) => (
                        <TableRow key={player.id}>
                          <TableCell>{player.playerIdCode || player.player_id_code || 'N/A'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                              {player.firstName || player.user?.firstName || player.user?.first_name || ''} {player.lastName || player.user?.lastName || player.user?.last_name || ''}
                            </Box>
                          </TableCell>
                          <TableCell>{player.email || player.user?.email || 'N/A'}</TableCell>
                          <TableCell>{player.primarySport || player.primary_sport || 'N/A'}</TableCell>
                          <TableCell>{player.level || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              icon={player.user?.lastLoginAt ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <CancelIcon sx={{ fontSize: 16 }} />}
                              label={player.user?.lastLoginAt ? "Yes" : "No"} 
                              size="small" 
                              color={player.user?.lastLoginAt ? "success" : "warning"}
                              title={player.user?.lastLoginAt ? "Player has logged in and is onboarded" : "Player has not logged in yet"}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Player">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingPlayer(player);
                                  setEditPlayerFormData({
                                    firstName: player.user?.firstName || player.firstName || '',
                                    lastName: player.user?.lastName || player.lastName || '',
                                    email: player.user?.email || player.email || '',
                                    dateOfBirth: player.dateOfBirth ? (typeof player.dateOfBirth === 'string' ? player.dateOfBirth.split('T')[0] : player.dateOfBirth) : '',
                                    gender: player.gender || 'male',
                                    country: player.country || '',
                                    primarySport: player.primarySport || '',
                                    secondarySport: player.secondarySport || '',
                                    level: player.level || 'beginner',
                                  });
                                  setEditPlayerDialogOpen(true);
                                  setGeneratedPassword(null);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add Player/Student Dialog */}
        <Dialog open={addPlayerDialogOpen} onClose={() => { setAddPlayerDialogOpen(false); resetPlayerForm(); }} maxWidth="md" fullWidth>
          <DialogTitle>Add Player / Student</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Registration Type Toggle */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Registration Type
                  </Typography>
                  <ToggleButtonGroup
                    value={isBelow18}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setIsBelow18(newValue);
                        // Reset parent fields when toggling
                        if (!newValue) {
                          setPlayerFormData({
                            ...playerFormData,
                            parent_email: '',
                            parent_phone: '',
                            parent_country: '',
                            father_name: '',
                            mother_name: '',
                          });
                        }
                      }
                    }}
                    aria-label="registration type"
                    fullWidth
                  >
                    <ToggleButton value={false} aria-label="regular player">
                      Regular Player (18+)
                    </ToggleButton>
                    <ToggleButton value={true} aria-label="below 18">
                      Player Below 18
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {isBelow18 
                      ? 'Registering for a player below 18 years. Parent/Guardian information required.'
                      : 'Registering for a player 18 years or older.'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    label="EncycGames Player ID (Optional)"
                    value={playerFormData.playerIdCode}
                    onChange={(e) => setPlayerFormData({ ...playerFormData, playerIdCode: e.target.value })}
                    helperText="If player is already registered, enter their Player ID here"
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSearchByPlayerId}
                    disabled={!playerFormData.playerIdCode || searchingPlayer}
                  >
                    {searchingPlayer ? 'Searching...' : 'Search & Load'}
                  </Button>
                </Box>
              </Grid>

              {isBelow18 && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    Player/Child Information
                  </Typography>
                </Grid>
              )}

              {foundPlayer && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Player found in database. Name, parent information, email, and phone are read-only to prevent unauthorized updates.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={isBelow18 ? "Name of your Child/Player *" : "First Name *"}
                  value={playerFormData.firstName}
                  onChange={(e) => setPlayerFormData({ ...playerFormData, firstName: e.target.value })}
                  disabled={!!foundPlayer}
                  required
                  helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  value={playerFormData.lastName}
                  onChange={(e) => setPlayerFormData({ ...playerFormData, lastName: e.target.value })}
                  disabled={!!foundPlayer}
                  required
                  helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                />
              </Grid>
              {!isBelow18 && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={playerFormData.email}
                    onChange={(e) => setPlayerFormData({ ...playerFormData, email: e.target.value })}
                    disabled={!!foundPlayer}
                    required
                    helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={playerFormData.dateOfBirth}
                  onChange={(e) => setPlayerFormData({ ...playerFormData, dateOfBirth: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={playerFormData.gender}
                    label="Gender"
                    onChange={(e) => setPlayerFormData({ ...playerFormData, gender: e.target.value })}
                  >
                    {genders.map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {gender.charAt(0).toUpperCase() + gender.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={playerFormData.country}
                  onChange={(e) => setPlayerFormData({ ...playerFormData, country: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Primary Sport</InputLabel>
                  <Select
                    value={playerFormData.primarySport}
                    label="Primary Sport"
                    onChange={(e) => setPlayerFormData({ ...playerFormData, primarySport: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {playerSports.map((sport) => (
                      <MenuItem key={sport} value={sport}>
                        {sport}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={playerFormData.level}
                    label="Level"
                    onChange={(e) => setPlayerFormData({ ...playerFormData, level: e.target.value })}
                  >
                    {levels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  value={playerFormData.grade}
                  onChange={(e) => setPlayerFormData({ ...playerFormData, grade: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Section"
                  value={playerFormData.section}
                  onChange={(e) => setPlayerFormData({ ...playerFormData, section: e.target.value })}
                />
              </Grid>

              {/* Parent/Guardian Information - Only for Below 18 */}
              {isBelow18 && (
                <>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Divider />
                    <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                      Parent/Guardian Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Parent Email Address *"
                      type="email"
                      value={playerFormData.parent_email}
                      onChange={(e) => setPlayerFormData({ ...playerFormData, parent_email: e.target.value })}
                      disabled={!!foundPlayer}
                      required
                      helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Parent Phone Number *"
                      value={playerFormData.parent_phone}
                      onChange={(e) => setPlayerFormData({ ...playerFormData, parent_phone: e.target.value })}
                      disabled={!!foundPlayer}
                      required
                      helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country *"
                      value={playerFormData.parent_country}
                      onChange={(e) => setPlayerFormData({ ...playerFormData, parent_country: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Father Name *"
                      value={playerFormData.father_name}
                      onChange={(e) => setPlayerFormData({ ...playerFormData, father_name: e.target.value })}
                      disabled={!!foundPlayer}
                      required
                      helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mother Name *"
                      value={playerFormData.mother_name}
                      onChange={(e) => setPlayerFormData({ ...playerFormData, mother_name: e.target.value })}
                      disabled={!!foundPlayer}
                      required
                      helperText={foundPlayer ? "Read-only: Player already exists in database" : ""}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddPlayerDialogOpen(false); resetPlayerForm(); }}>Cancel</Button>
            <Button onClick={handleAddPlayer} variant="contained" disabled={loadingPlayers}>
              {loadingPlayers ? 'Adding...' : 'Add Player'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Player Dialog */}
        <Dialog open={editPlayerDialogOpen} onClose={() => { setEditPlayerDialogOpen(false); setEditingPlayer(null); setGeneratedPassword(null); }} maxWidth="md" fullWidth>
          <DialogTitle>Edit Player</DialogTitle>
          <DialogContent>
            {editingPlayer && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={editPlayerFormData.firstName}
                    onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, firstName: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    value={editPlayerFormData.lastName}
                    onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, lastName: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={editPlayerFormData.email}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={editPlayerFormData.dateOfBirth}
                    onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, dateOfBirth: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={editPlayerFormData.gender}
                      label="Gender"
                      onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, gender: e.target.value })}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={editPlayerFormData.country}
                    onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, country: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Primary Sport"
                    value={editPlayerFormData.primarySport}
                    onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, primarySport: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Secondary Sport"
                    value={editPlayerFormData.secondarySport}
                    onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, secondarySport: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Level</InputLabel>
                    <Select
                      value={editPlayerFormData.level}
                      label="Level"
                      onChange={(e) => setEditPlayerFormData({ ...editPlayerFormData, level: e.target.value })}
                    >
                      <MenuItem value="beginner">Beginner</MenuItem>
                      <MenuItem value="intermediate">Intermediate</MenuItem>
                      <MenuItem value="advanced">Advanced</MenuItem>
                      <MenuItem value="professional">Professional</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
            
            {/* Generate Password Section - Only show if user was created by coach and hasn't logged in */}
            {editingPlayer && editingPlayer.canGeneratePassword !== false && !editingPlayer.user?.lastLoginAt && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Password Management
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This player hasn't logged in yet. You can generate a password for them. Once they log in, they will control their own password.
                </Alert>
                {generatedPassword ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Password Generated Successfully!
                    </Typography>
                    <Typography variant="body2" component="div">
                      <strong>New Password:</strong>
                      <Box 
                        sx={{ 
                          mt: 1, 
                          p: 2, 
                          bgcolor: 'grey.100', 
                          borderRadius: 1, 
                          fontFamily: 'monospace',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'text',
                          userSelect: 'text'
                        }}
                        onClick={(e) => {
                          const text = e.currentTarget.querySelector('span')?.textContent || generatedPassword;
                          navigator.clipboard.writeText(text || '');
                          toast.success('Password copied to clipboard!');
                        }}
                      >
                        <span style={{ flex: 1 }}>{generatedPassword}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(generatedPassword || '');
                            toast.success('Password copied to clipboard!');
                          }}
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Click the password or copy icon to copy. Please share this password with the player securely. They should change it after first login.
                    </Typography>
                  </Alert>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<SecurityIcon />}
                    onClick={async () => {
                      if (!editingPlayer) return;
                      try {
                        const coachId = (user as any)?.coachId;
                        const response = await fetch(`/api/players/${editingPlayer.id}/generate-password`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ coachId })
                        });
                        const data = await response.json();
                        if (data.success) {
                          setGeneratedPassword(data.data.password);
                          toast.success('Password generated successfully!');
                        } else {
                          toast.error(data.message || 'Failed to generate password');
                        }
                      } catch (err: any) {
                        toast.error('Failed to generate password: ' + err.message);
                      }
                    }}
                    sx={{ mb: 2 }}
                  >
                    Generate Password
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  If the player loses this password, they can use the "Forgot Password" feature on the login page with their Player ID and email.
                </Typography>
              </Box>
            )}
            
            {editingPlayer && (editingPlayer.canGeneratePassword === false || editingPlayer.user?.lastLoginAt) && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Alert severity="info">
                  {editingPlayer.user?.lastLoginAt 
                    ? "This player has logged in and is active. They control their own password. If they need to reset it, they should use the 'Forgot Password' feature on the login page."
                    : "This player was registered independently. They control their own password. If they need to reset it, they should use the 'Forgot Password' feature on the login page with their Player ID and email."}
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { 
              setEditPlayerDialogOpen(false); 
              setEditingPlayer(null); 
              setGeneratedPassword(null);
            }}>
              {generatedPassword ? 'Close' : 'Cancel'}
            </Button>
            {!generatedPassword && (
              <Button
                variant="contained"
                onClick={async () => {
                  if (!editingPlayer) return;
                  try {
                    const coachId = (user as any)?.coachId;
                    const response = await fetch(`/api/players/${editingPlayer.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        firstName: editPlayerFormData.firstName,
                        lastName: editPlayerFormData.lastName,
                        dateOfBirth: editPlayerFormData.dateOfBirth || undefined,
                        gender: editPlayerFormData.gender,
                        country: editPlayerFormData.country,
                        primarySport: editPlayerFormData.primarySport,
                        secondarySport: editPlayerFormData.secondarySport,
                        level: editPlayerFormData.level,
                        coachId
                      })
                    });
                    const data = await response.json();
                    if (data.success) {
                      toast.success('Player updated successfully!');
                      setEditPlayerDialogOpen(false);
                      setEditingPlayer(null);
                      setGeneratedPassword(null);
                      loadPlayers();
                    } else {
                      toast.error(data.message || 'Failed to update player');
                    }
                  } catch (err: any) {
                    toast.error('Failed to update player: ' + err.message);
                  }
                }}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Render Academies list (default view or when activeView is 'academies')
  if (activeView === 'academies' || activeView === 'academy-management') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Academy Management
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            console.log('🔄 Manual refresh triggered for academies');
            loadAcademies();
          }}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Academies List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Academies
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Head Coach</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {academies.map((academy) => (
                    <TableRow key={academy.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AcademyIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {academy.name}
                        </Box>
                      </TableCell>
                      <TableCell>{academy.description}</TableCell>
                      <TableCell>{academy.address}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{academy.contact_email}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {academy.contact_phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {academy.head_coach_id === (user as any)?.coachId ? (
                          <Chip label="You" color="success" size="small" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Academy Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAcademy ? 'Edit Academy' : 'Add New Academy'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Academy Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAcademy ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    );
  }

  // Default fallback (should not reach here)
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4">Academy Management</Typography>
      <Typography color="text.secondary">Select a view from the sidebar</Typography>
    </Container>
  );
};

export default AcademyManagement;
